import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import {
    BookingModule,
    Company,
    CompanyCentralizedPayment,
    CompanyCentralizedPaymentMode,
    CompanyMensualCentralizedPaymentMode,
    CostCenter,
    DispatchGroup,
    Driver,
    Rider,
    Vehicle,
} from '../redux/reducers/api-types';
import { FormPickerDatas, PickerOption } from '../local-types';
import { MAX_MAD_TIME } from './constants';
import CoreAPIService from './core-api';
import UserUtils from './userUtils';

//================================================//
//================ Payment =======================//
//================================================//

// French IBANs only (FR, 2 check digits, 23 alphanumerics), as shown by formatIban: groups of 4
export const IBAN_INPUT_PATTERN = '^FR[0-9]{2}( [0-9A-Z]{4}){5} [0-9A-Z]{3}$';
export const IBAN_REGEX = /^FR[0-9]{2}[0-9A-Z]{23}$/;
const IBAN_MAX_LENGTH = 34;

export function normalizeIban(iban?: string): string | undefined {
    return typeof iban === 'string' ? iban.replace(/\s/g, '').toUpperCase() : iban;
}

export function formatIban(iban?: string): string {
    return (normalizeIban(iban) ?? '').replace(/(.{4})(?=.)/g, '$1 ');
}

/**
 * Reformats a typed or pasted IBAN in groups of 4. The caret is placed by counting the
 * characters before it, so the spaces added or dropped around it do not move it.
 */
export function editIban(raw: string, caret: number, previous?: string, inputType?: string) {
    let compact = normalizeIban(raw) as string;
    let before = (normalizeIban(raw.slice(0, caret)) as string).length;
    if (compact === normalizeIban(previous ?? '') && raw.length < formatIban(previous).length) {
        // Only a group separator was erased: erase the character next to it instead
        if (inputType === 'deleteContentForward') {
            compact = compact.slice(0, before) + compact.slice(before + 1);
        } else if (before > 0) {
            compact = compact.slice(0, before - 1) + compact.slice(before);
            before -= 1;
        }
    }
    compact = compact.slice(0, IBAN_MAX_LENGTH);
    before = Math.min(before, compact.length);
    return { value: formatIban(compact), caret: before + Math.floor(Math.max(before - 1, 0) / 4) };
}

export function getPaymentModeDetails(payload: Company, t) {
    let centralizedPaymentMode = 'N/A';
    let centralizedMensualPaymentMode = 'N/A';
    if (payload.centralized_payment === CompanyCentralizedPayment.central) {
        if (payload.centralized_payment_mode === CompanyCentralizedPaymentMode.mensual) {
            centralizedPaymentMode = t('paiement.mensual');
            if (payload.centralized_mensual_payment_mode === CompanyMensualCentralizedPaymentMode.sepa) {
                centralizedMensualPaymentMode = t('paiement.sepa');
            } else if (
                payload.centralized_mensual_payment_mode === CompanyMensualCentralizedPaymentMode.wire_transfer
            ) {
                centralizedMensualPaymentMode = t('paiement.transfert');
            } else if (payload.centralized_mensual_payment_mode === CompanyMensualCentralizedPaymentMode.airplus) {
                centralizedMensualPaymentMode = t('paiement.airplus');
            } else {
                centralizedMensualPaymentMode = `${t('paiement.cb')} ${
                    payload.centralized_mensual_payment_mode_cb_mask
                }`;
            }
        } else {
            centralizedPaymentMode = t('paiement.unitary');
        }
    }
    return { centralizedPaymentMode, centralizedMensualPaymentMode };
}

//===============================================//
//=============== Dispatch Groups ===============//
//===============================================//

export function getDispatchGroupOptions(dispatchGroups: DispatchGroup[]): PickerOption[] {
    let dispatchGroupOptions: PickerOption[] = [];
    if (dispatchGroups.length > 0) {
        dispatchGroupOptions = [
            {
                value: '',
                label: '',
            },
            ...dispatchGroups.map((dg: DispatchGroup) => {
                return { value: dg.id, label: dg.name };
            }),
        ];
    }

    return dispatchGroupOptions;
}

//================================================//
//=============== Picker Generator ===============//
//================================================//

export function generateOptions(
    items: Array<any> = [],
    valueKey: string = 'key',
    labelKey: string = 'name',
): PickerOption[] {
    return [
        { value: '', label: '' },
        ...items.map((item) => ({
            value: `${get(item, valueKey, '')}`,
            label: `${get(item, labelKey, '')}`,
        })),
    ];
}

export function generateOptionsString(t, items: string[], translationPrefix: string): PickerOption[] {
    return [
        { value: '', label: '' },
        ...items.map((status) => ({
            value: status,
            label: t(`${translationPrefix}${status}`),
        })),
    ];
}

export function generateFormOptions(
    items: Array<any> = [],
    valueKey: string = 'key',
    labelKey: string = 'name',
): FormPickerDatas[] {
    return [
        ...items.map((item) => ({
            id: `${get(item, valueKey, '')}`,
            name: `${get(item, labelKey, '')}`,
        })),
    ];
}

//========================================//
//=============== Vehicles ===============//
//========================================//

export function getVehicleDetails(vehiclePayload: Vehicle): string {
    if (!vehiclePayload) {
        return '';
    }

    const { model, color, vehicle_model, vehicle_color, registration_number } = vehiclePayload;
    const brandName = vehicle_model?.brand.name ?? '';
    const modelName = (model || vehicle_model?.name) ?? '';
    const colorName = (color || vehicle_color?.name) ?? '';

    if (model || color) {
        console.warn('This payload has model or color', vehiclePayload);
    }

    return `${brandName} ${modelName} ${colorName} (${registration_number})`.trim();
}

export function getVehicleModel(vehiclePayload: Vehicle): string {
    if (!vehiclePayload) {
        return '';
    }

    const { vehicle_model } = vehiclePayload;
    const brandName = vehicle_model?.brand.name ?? '';
    const modelName = vehicle_model?.name ?? '';

    return `${brandName} ${modelName}`.trim();
}

//=====================================//
//=============== Users ===============//
//=====================================//

export function getBanReason(t, riderPayload: Rider | Driver): string {
    return riderPayload.ban_reason &&
        (riderPayload.ban_reason === 'conformity' || riderPayload.ban_reason === 'new_user')
        ? t('driver:ban_reason.' + riderPayload.ban_reason)
        : t('driver:ban_reason.manual');
}

//===========================================//
//=============== CostCenters ===============//
//===========================================//

export function getCostCenterDetails(costCenter: CostCenter): string {
    if (!costCenter) {
        return '';
    }
    const { name, description } = costCenter;
    return `${name ?? ''}${description ? ` - ${description}` : ''}`;
}

//===================================//
//=============== Mad ===============//
//===================================//

export function generateMadOptions(): FormPickerDatas[] {
    const optionsMad: FormPickerDatas[] = Array(MAX_MAD_TIME)
        .fill(0)
        .map((_, i) => {
            return { id: '' + (i + 1) * 60, name: i + 1 + 'h' };
        });
    return optionsMad;
}

//=====================================//
//=============== Convert =============//
//=====================================//

export function trimObjectString(obj: Record<string, any>): Record<string, any> {
    const newObj = { ...obj };

    Object.keys(newObj).forEach((key) => {
        if (typeof newObj[key] === 'string') {
            newObj[key] = newObj[key].trim();
        } else if (Array.isArray(newObj[key])) {
            newObj[key] = newObj[key].map((item) => {
                if (typeof item === 'string') {
                    return item.trim();
                }
                return item;
            });
        } else if (typeof newObj[key] === 'object' && !isEmpty(newObj[key])) {
            // Recursively trim nested objects
            newObj[key] = trimObjectString(newObj[key]);
        }
    });

    return newObj;
}

//=======================================//
//=============== Documents =============//
//=======================================//

export function generateDocumentUrl(id: string) {
    return CoreAPIService.BASE_URL + '/doc/' + id + '?token=' + UserUtils.getToken();
}
